import React, { useState } from "react";
import { Smile} from "lucide-react";
import { Post } from "../../../types/posts";
import { YouTubeVideoComment } from "./YouTubeVideoComment";

export default function YouTubeFooter({ post }: { post: Post }) {
  const [comment, setComment] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const DESCRIPTION_LIMIT = 296;

  const handleCancel = () => {
    setComment("");
    setIsFocused(false);
  };

  const getChannelInitials = (name?: string) => {
    if (!name) return "YT";
    const parts = name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
    return parts.slice(0, 2) || "YT";
  };

 const description = `${post?.content || ""}${
   post?.footer && post.footerType === "in-post" ? "\n\n" + post.footer : ""
 }${
   // Hashtags are now part of content directly — appending from youtubeHashtags field is disabled.
   // Array.isArray(post?.youtubeHashtags) && post.youtubeHashtags.length > 0
   //   ? "\n\n" + post.youtubeHashtags.join(" ")
   //   : ""
   ""
 }`;

  const isLongDescription = description.length > DESCRIPTION_LIMIT;

  const contentStr = Array.isArray(post?.content) ? post.content.join(" ") : (post?.content || "");
  const topHashtags = (contentStr.match(/#\w+/g) || []).slice(0, 3);

  const truncatedDescription =
    !expanded && isLongDescription
      ? description.slice(0, DESCRIPTION_LIMIT) + "..."
      : description;

        // Get the date for relative time
  const postDate = post.postTime
    ? new Date(post.postTime)
    : post.scheduledFor
    ? new Date(post.scheduledFor)
    : null;

  return (
    <>
      {/* DESCRIPTION BOX */}
      <div
        className={` rounded-xl p-4 mb-6 text-sm ${
          !expanded ? "bg-[#f2f2f2] hover:bg-[#fff8f0]" : "bg-[#f2f2f2]"
        }`}
      >
        {/* Row 1 */}
        <div className="font-semibold text-gray-900 mb-2 flex flex-wrap items-center gap-2">
          <span>3 views</span>

          <span>
            {postDate && (
              <>
                {postDate.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </>
            )}
          </span>

          {topHashtags.length > 0 && (
            <span className="flex flex-wrap gap-1">
              {topHashtags.map((tag: string) => (
                <span key={tag} className={`cursor-pointer ${expanded ? "text-blue-600" : "text-[#6F6F6F]"}`}>{tag}</span>
              ))}
            </span>
          )}
        </div>

        {/* Row 2 */}
        {((post as any)?.contentType === "Caption Video" || (post as any)?.type === "Caption Video") &&
        !post?.content &&
        (post?.status?.toLowerCase() === "draft" || post?.status?.toLowerCase() === "videoprocessing") ? (
          <div className="space-y-2 py-1 mb-3">
            <div className="h-3 bg-gray-200 rounded w-3/4 shimmer-bar" />
            <div className="h-3 bg-gray-200 rounded w-1/2 shimmer-bar" />
          </div>
        ) : (
        <p className="text-gray-800 mb-3 leading-relaxed whitespace-pre-line break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
          {expanded
            ? truncatedDescription.split(/(#\w+)/g).map((part, i) =>
                part.startsWith("#") ? (
                  <span key={i} className="text-blue-600 cursor-pointer">{part}</span>
                ) : (
                  part
                )
              )
            : truncatedDescription}

          {!expanded && isLongDescription && (
            <button
              onClick={() => setExpanded(true)}
              className="ml-1 font-semibold text-gray-900"
            >
              more
            </button>
          )}
        </p>
        )}

        {/* BELOW CONTENT ONLY WHEN EXPANDED */}

        {expanded && (
          <>
            {/* Row 3 - Channel */}
            <div className="flex items-center gap-3 mb-4">
              {post?.userImage ? (
                <img
                  src={post.userImage}
                  alt="User"
                  className="rounded-full object-cover w-9 h-9"
                />
              ) : (
                <span className="text-sm font-semibold flex items-center justify-center w-9 h-9 bg-gray-300 rounded-full">
                  {getChannelInitials(post?.displayName)}
                </span>
              )}

              <span className="font-semibold text-gray-900">
                {post?.displayName || "YouTube Channel"}
              </span>
            </div>

            {/* Row 5 - Tabs */}
            <div className={`flex gap-2 mb-3`}>
              <button
                className={`font-medium flex items-center gap-2 text-sm text-black hover:bg-[#dadada] bg-[#f2f2f2] px-2 py-1 border-[#0000001a] border rounded-full`}
              >
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    width="24"
                    viewBox="0 0 24 24"
                    focusable="false"
                    aria-hidden="true"
                    style={{
                      pointerEvents: "none",
                      display: "inherit",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <path d="M21 3H3a2 2 0 00-2 2v14a2 2 0 002 2h18a2 2 0 002-2V5a2 2 0 00-2-2ZM3 19V5h18v14H3Zm13-7L9.5 8v8l6.5-4Z" />
                  </svg>
                </div>
                Videos
              </button>

              <button
                className={`font-medium flex items-center gap-2 text-sm text-black hover:bg-[#dadada] bg-[#f2f2f2] px-2 py-1 border-[#0000001a] border rounded-full`}
              >
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    width="24"
                    viewBox="0 0 24 24"
                    focusable="false"
                    aria-hidden="true"
                    style={{
                      pointerEvents: "none",
                      display: "inherit",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <path d="M20 2H4a2 2 0 00-2 2v16a2 2 0 002 2h16a2 2 0 002-2V4a2 2 0 00-2-2ZM4 20V4h16v16h-1.293a7 7 0 00-13.414 0H4Zm8-14a4 4 0 100 8 4 4 0 000-8Zm0 2a2 2 0 110 4 2 2 0 010-4Zm-.1 9.001h-.002A5 5 0 0116.581 20H7.417a5 5 0 014.483-2.999Z" />
                  </svg>
                </div>
                About
              </button>
            </div>

            {/* Row 6 */}
            <button
              onClick={() => setExpanded(false)}
              className="text-sm font-medium text-gray-700 hover:text-black mt-4"
            >
              Show less
            </button>
          </>
        )}
      </div>

      {/* COMMENTS */}
      {post.footerType === "comment" && post?.footer && <div className="mx-auto mt-8">
        <div className="flex items-center gap-4 mb-6">
          <h3 className="font-semibold text-lg">0 Comments</h3>

          <button className="flex items-center gap-2 text-sm text-gray-600 hover:text-black">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                width="24"
                viewBox="0 0 24 24"
                focusable="false"
                aria-hidden="true"
                style={{
                  pointerEvents: "none",
                  display: "inherit",
                  width: "100%",
                  height: "100%",
                }}
              >
                <path d="M21 5H3a1 1 0 000 2h18a1 1 0 100-2Zm-6 6H3a1 1 0 000 2h12a1 1 0 000-2Zm-6 6H3a1 1 0 000 2h6a1 1 0 000-2Z" />
              </svg>
            </div>
            Sort by
          </button>
        </div>

        <div className="flex gap-3">
          {post?.userImage ? (
            <img
              src={post.userImage}
              alt="User"
              className="rounded-full object-cover w-9 h-9"
            />
          ) : (
            <span className="text-base font-semibold flex items-center justify-center w-9 h-9 bg-gray-300 rounded-full">
              {getChannelInitials(post?.displayName)}
            </span>
          )}

          <div className="flex-1">
            <input
              type="text"
              placeholder="Add a comment..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onFocus={() => setIsFocused(true)}
              className="w-full border-b border-gray-300 outline-none pb-2 focus:border-black text-sm"
            />

            {isFocused && (
              <div className="flex items-center justify-between mt-3">
                <button className="text-gray-500 hover:text-black">
                  <Smile size={18} />
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-black"
                  >
                    Cancel
                  </button>

                  <button
                    disabled={!comment.trim()}
                    className={`px-4 py-1 text-sm rounded-full ${
                      comment.trim()
                        ? "bg-black text-white"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Comment
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
        <div className="pt-4">
          <YouTubeVideoComment post={post} />
        </div>
      </div>}
    </>
  );
}
